import UnfoldLess from '@mui/icons-material/UnfoldLess';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import { FetchReason } from '~constants/enums';
import BulkCreateSettlementsMenu from '~pages/Approvals/DriverPayV2/components/CTAs/BulkCreateSettlementsMenu';
import BulkDownloadFilesCTA from '~pages/Approvals/Files/components/CTAs/BulkDownloadFilesCTA';
import ExportCsvCTA from '~pages/Approvals/Files/components/CTAs/ExportCsvCTA';
import OrderSelectorCTA from '~pages/Approvals/Files/components/CTAs/OrderSelectorCTA';
import RefreshCTA from '~pages/Approvals/Files/components/CTAs/RefreshCTA';
import DateRangeFilter from '~pages/Approvals/Files/components/Filters/DateRangeFilter';
import OrdersFilters, {
  FilesAvailableFilter,
} from '~pages/Approvals/Files/components/Filters/OrdersFilters';
import Search from '~pages/Approvals/Files/components/Filters/Search';
import { GridContainer, GridRow } from '~pages/Approvals/Files/components/styled';
import { FilesQueryOrdersSort } from '~pages/Approvals/Files/constants';
import FilesPageContext, {
  FilesPageContextProvider,
} from '~pages/Approvals/Files/FilesPageContext';
import FilesQueryContext, {
  deriveFilterQueryParams,
  FilesQueryContextProvider,
} from '~pages/Approvals/Files/FilesQueryContext';
import useApproveChargesSummaryQuery from '~queries/useApproveChargesSummaryQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ApproveChargesPageContext, {
  ApproveChargesPageContextProvider,
} from './ApproveChargesPageContext';
import { ChargesException, ChargesGrainTab } from './approveChargesTabs';
import {
  chargesListCardSx,
  HOURLY_TIME_AND_DURATION_TRACK,
  TIME_AND_DURATION_TRACK_VARIABLE,
} from './chargesGrid';
import { ChargesGroupBy } from './chargesGrouping';
import {
  ChargesSelectionActionsContext,
  ChargesSelectionContextProvider,
} from './ChargesSelectionContext';
import ChargesExceptionChips from './components/ChargesExceptionChips';
import ChargesGrainTabs from './components/ChargesGrainTabs';
import ChargesGroupedList from './components/ChargesGroupedList';
import ChargesGroupMenu from './components/ChargesGroupMenu';
import ChargesJobsHeader from './components/ChargesJobsHeader';
import ChargesOrdersList from './components/ChargesOrdersList';
import ChargesSelectionBar from './components/ChargesSelectionBar';
import ChargesShowMenu from './components/ChargesShowMenu';
import ChargesSummaryTiles from './components/ChargesSummaryTiles';
import ChargesViewTabs from './components/ChargesViewTabs';

// Browser storage entries of this page are kept apart from Approve Jobs, which shares
// the filters provider
const STORAGE_NAMESPACE = 'approve_charges';

const FILTERS_REPLACED_BY_CHARGES_VIEW = [
  FilesAvailableFilter.AI_REVIEW_STATUSES,
  FilesAvailableFilter.JOB_APPROVAL_STATUS,
  FilesAvailableFilter.LOAD_APPROVAL_STATUS,
];

const SCHEDULED_AT_NEWEST_FIRST: OrderOption<FilesQueryOrdersSort> = {
  orderBy: FilesQueryOrdersSort.LOAD_AT,
  sortBy: 'desc',
};

export default observer(function ApproveChargesContainer() {
  return (
    <ApproveChargesPageContextProvider>
      <ChargesSelectionContextProvider>
        <ApproveChargesProviders />
      </ChargesSelectionContextProvider>
    </ApproveChargesPageContextProvider>
  );
});

const ApproveChargesProviders = observer(function ApproveChargesProviders() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { ordersQuery } = useContext(ApproveChargesPageContext);

  return (
    <FilesQueryContextProvider
      companyId={companyId}
      extraOrdersQuery={ordersQuery}
      initialOrdersSort={SCHEDULED_AT_NEWEST_FIRST}
      storageNamespace={STORAGE_NAMESPACE}
    >
      <FilesPageContextProvider>
        <ApproveCharges />
      </FilesPageContextProvider>
    </FilesQueryContextProvider>
  );
});

const ApproveCharges = observer(function ApproveCharges() {
  const filesQuery = useContext(FilesQueryContext);
  const filesPage = useContext(FilesPageContext);
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const {
    exception,
    expandAllRequest,
    grainTab,
    isAiApprovedOnly,
    ordersQuery,
    requestExpandAll,
    settledSide,
    showOption,
    toggleException,
    viewTab,
  } = useContext(ApproveChargesPageContext);
  const isExpandAllRequested = expandAllRequest?.isExpanded ?? false;
  const expandAllLabel = isExpandAllRequested
    ? t('dispatch.dispatch_v2.collapse_all')
    : t('dispatch.dispatch_v2.expand_all');
  const isIssuesFilterApplied = Boolean(
    filesQuery.filters.ticketPotentialDuplicate?.length,
  );

  useEffect(() => {
    if (exception === ChargesException.ISSUES && !isIssuesFilterApplied) {
      toggleException(ChargesException.ISSUES);
    }
  }, [exception, isIssuesFilterApplied, toggleException]);

  const summaryQuery = useMemo(
    () => ({ ...deriveFilterQueryParams(filesQuery.filters), ...ordersQuery }),
    [filesQuery.filters, ordersQuery],
  );
  const { isError, isLoading, summary } = useApproveChargesSummaryQuery(
    companyId,
    summaryQuery,
    filesQuery.refreshCount,
  );
  const { clearQueryData, fetchReason } = filesQuery;
  const { reset } = filesPage;
  const { uncheckAllLines } = useContext(ChargesSelectionActionsContext);
  const { refresh: refreshFiles } = filesQuery;
  const handleBulkCreateFinished = useCallback(() => {
    uncheckAllLines();
    refreshFiles();
  }, [refreshFiles, uncheckAllLines]);
  const chargesViewKey = JSON.stringify([
    exception,
    summaryQuery,
    grainTab,
    isAiApprovedOnly,
    settledSide,
    showOption,
    viewTab,
  ]);

  useEffect(() => {
    uncheckAllLines();
  }, [chargesViewKey, uncheckAllLines]);

  useEffect(() => {
    if (fetchReason === FetchReason.INITIAL_FETCH) {
      reset();
    }
  }, [fetchReason, reset]);

  useEffect(() => clearQueryData, [clearQueryData]);

  return (
    <GridContainer variant="approve-charges">
      <GridRow sx={{ mt: '-1px' }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <DateRangeFilter />

          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <ExportCsvCTA />
            <RefreshCTA />
            <BulkDownloadFilesCTA />

            <BulkCreateSettlementsMenu
              description={t(
                'approvals.approve_charges.bulk_create_settlements_description',
              )}
              endDate={filesQuery.filters.endDate}
              onBulkCreateFinished={handleBulkCreateFinished}
              startDate={filesQuery.filters.startDate}
            />
          </Box>
        </Box>
      </GridRow>

      <GridRow>
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <ChargesGrainTabs />
          <ChargesViewTabs />

          <Box sx={{ flex: 0, minWidth: '180px' }}>
            <Search />
          </Box>

          <Box
            data-test-id="charges-filters"
            sx={(theme) => ({
              alignItems: 'center',
              bgcolor: theme.brandV2.colors.treadGray9,
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              borderRadius: theme.spacing(),
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              px: 0.75,
              py: 0.5,
            })}
          >
            <OrdersFilters hiddenFilters={FILTERS_REPLACED_BY_CHARGES_VIEW} />
            <ChargesShowMenu />
            <ChargesExceptionChips />
          </Box>

          <Box sx={{ flex: 1 }} />

          <ChargesGroupMenu />
          <OrderSelectorCTA />
          <Tooltip title={expandAllLabel}>
            <span>
              <IconButton
                aria-label={expandAllLabel}
                disabled={grainTab === ChargesGrainTab.HOURLY_JOBS}
                onClick={() => requestExpandAll(!isExpandAllRequested)}
                size="small"
                sx={(theme) => ({
                  border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                  borderRadius: 1,
                })}
              >
                {isExpandAllRequested ? (
                  <UnfoldLess fontSize="small" />
                ) : (
                  <UnfoldMore fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </GridRow>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: 2,
          py: 1.5,
          ...(grainTab === ChargesGrainTab.HOURLY_JOBS && {
            [TIME_AND_DURATION_TRACK_VARIABLE]: HOURLY_TIME_AND_DURATION_TRACK,
          }),
        }}
      >
        <ChargesSummaryTiles isError={isError} isLoading={isLoading} summary={summary} />
        <ChargesList />
      </Box>

      <ChargesSelectionBar />
    </GridContainer>
  );
});

function ChargesList() {
  const filesQuery = useContext(FilesQueryContext);
  const { groupBy } = useContext(ApproveChargesPageContext);
  const isFirstRead = filesQuery.fetchReason === FetchReason.INITIAL_FETCH;
  const isReadingNextPage = filesQuery.fetchReason === FetchReason.INFINITE_SCROLL;

  if (isFirstRead) {
    return (
      <Box sx={chargesListCardSx}>
        <LoadingSpinner isVisible sx={{ my: 1.5 }} />
      </Box>
    );
  }

  if (groupBy !== ChargesGroupBy.NONE) {
    return (
      <ChargesGroupedList
        groupBy={groupBy}
        hasMore={filesQuery.hasMore}
        hasUnfilteredResults={filesQuery.hasUnfilteredResults}
        isLoading={isReadingNextPage}
        onClearFilters={filesQuery.clearAllFiltersExceptSearch}
        onLoadMore={filesQuery.fetchMore}
        orders={filesQuery.data}
      />
    );
  }

  return (
    <Box sx={chargesListCardSx}>
      <ChargesJobsHeader />
      <ChargesOrdersList
        hasMore={filesQuery.hasMore}
        hasUnfilteredResults={filesQuery.hasUnfilteredResults}
        isLoading={isReadingNextPage}
        onClearFilters={filesQuery.clearAllFiltersExceptSearch}
        onLoadMore={filesQuery.fetchMore}
        orders={filesQuery.data}
      />
    </Box>
  );
}
